import { Box, Skeleton, styled, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSidebar } from "../context";
import {
  useResizableInfoPanel,
  useResizableSidebar,
  useResponsiveHeight,
} from "../hooks";
import { PanelType } from "../interfaces/all";
import ChatDialog from "./chat/ChatDialog/ChatDialog";
import ChatSidebar from "./chat/ChatSidebar/ChatSidebar";
import InfoPanel from "./chat/InfoPanel/InfoPanel";
import LeftSidebar from "./chat/LeftSidebar/LeftSidebar";
import { AIControlManager, PhraseConfigManager } from "./drawer/ai-control";
import { AutomationManager } from "./drawer/automation";
import { BulkSendManager } from "./drawer/bulk-send";
import { LeadContacts } from "./drawer/lead-contacts";
import { CompanyProfile, CompanySettings, UserProfile } from "./drawer/profile";
import { TemplatesSidebar } from "./drawer/templates";
import ResizeHandle from "./shared/ResizeHandle";

const Container = styled(Box)`
  display: flex;
  height: 100vh;
  max-height: 100vh; /* Limitar altura máxima */
  width: 100vw;
  background: #f0f2f5;
  overflow: hidden;
  position: relative;

  /* Correções específicas para mobile */
  @media screen and (max-width: 768px) {
    height: 100vh;
    height: -webkit-fill-available;
    height: 100dvh;
    max-height: 100vh;
    max-height: -webkit-fill-available;
    max-height: 100dvh;
    min-height: 100vh;
    min-height: -webkit-fill-available;
    min-height: 100dvh;
  }

  /* Suporte para navegadores modernos */
  @supports (height: 100dvh) {
    @media screen and (max-width: 768px) {
      height: 100dvh;
      max-height: 100dvh;
      min-height: 100dvh;
    }
  }
`;

const MainContent = styled(Box)`
  display: flex;
  flex: 1;
  min-width: 0;
  position: relative;
  background: #f0f2f5;
  height: 100vh; /* Altura fixa para desktop */
  max-height: 100vh; /* Limitar altura máxima */
  overflow: hidden; /* Prevenir scroll no container principal */

  @media (max-width: 600px) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 50px;
    width: calc(100% - 50px);
    height: 100vh;
    height: -webkit-fill-available;
    height: 100dvh;
    max-height: 100vh;
    max-height: -webkit-fill-available;
    max-height: 100dvh;
    transition:
      left 0.3s ease,
      width 0.3s ease;
  }

  /* Suporte para navegadores modernos */
  @supports (height: 100dvh) {
    @media (max-width: 600px) {
      height: 100dvh;
      max-height: 100dvh;
    }
  }
`;

const SidebarContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  background: #fff;
  flex-shrink: 0;
  height: 100vh; /* Altura fixa para desktop */
  max-height: 100vh; /* Limitar altura máxima */
  overflow: hidden; /* Prevenir scroll no container principal */

  @media (max-width: 600px) {
    position: absolute;
    top: 0;
    left: 50px;
    width: calc(100% - 50px);
    height: 100vh;
    height: -webkit-fill-available;
    height: 100dvh;
    max-height: 100vh;
    max-height: -webkit-fill-available;
    max-height: 100dvh;
    z-index: 1000; /* Z-index muito alto para garantir que apareça acima de todos os outros elementos */
    transition:
      left 0.3s ease,
      width 0.3s ease;
    /* Garantir visibilidade */
    visibility: visible !important;
    opacity: 1 !important;
    display: flex !important;
  }

  /* Suporte para navegadores modernos */
  @supports (height: 100dvh) {
    @media (max-width: 600px) {
      height: 100dvh;
      max-height: 100dvh;
    }
  }
`;

const DialogWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isHidden",
})<{ isHidden?: boolean }>(({ isHidden }) => ({
  flex: isHidden ? 0 : 1,
  minWidth: isHidden ? 0 : "auto",
  width: isHidden ? 0 : "auto",
  overflow: "hidden",
  display: isHidden ? "none" : "flex",
  flexDirection: "column",
  flexGrow: 1,
  flexShrink: 1,
  position: "relative",
}));

const InfoPanelWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isSmallScreen" &&
    prop !== "isSearchOpen" &&
    prop !== "isResizing",
})<{ isSmallScreen?: boolean; isSearchOpen?: boolean; isResizing?: boolean }>(
  ({ isSmallScreen, isSearchOpen, isResizing = false }) => ({
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#ffffff",
    borderLeft: "1px solid #e9edef",
    transform: isSearchOpen ? "translateX(0)" : "translateX(100%)",
    transition: isResizing ? "none" : "transform 0.3s ease-in-out",
    zIndex:
      isSmallScreen && isSearchOpen
        ? 1
        : 1 /* Z-index baixo para não sobrepor o CollapseButton (9999) */,
  }),
);

const Messenger: React.FC = () => {
  const [showSkeleton, setShowSkeleton] = useState(true);
  const { sidebarType } = useSidebar();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchType, setSearchType] = useState<PanelType>("search");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { getHeightStyles } = useResponsiveHeight();

  // Determina se deve mostrar o InfoPanel baseado no tipo de sidebar
  const shouldShowInfoPanel = searchOpen;

  // Hook para redimensionamento do sidebar (lado esquerdo)
  const {
    width: sidebarWidth,
    isResizing: isSidebarResizing,
    handleMouseDown: handleSidebarMouseDown,
    handleDoubleClick: handleSidebarDoubleClick,
    minWidth: sidebarMinWidth,
    maxWidth: sidebarMaxWidth,
  } = useResizableSidebar(sidebarType);

  // Hook para redimensionamento do InfoPanel (lado direito)
  // O hook sempre retorna valores válidos, mesmo quando o painel está fechado
  const {
    width: infoPanelWidth,
    isResizing: isInfoPanelResizing,
    handleMouseDown: handleInfoPanelMouseDown,
    handleDoubleClick: handleInfoPanelDoubleClick,
    minWidth: infoPanelMinWidth,
    maxWidth: infoPanelMaxWidth,
  } = useResizableInfoPanel(shouldShowInfoPanel);

  // Simular loading inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 1500);

    // Cleanup para evitar race condition se componente for desmontado
    return () => {
      clearTimeout(timer);
      setShowSkeleton(false);
    };
  }, []);

  if (showSkeleton) {
    return (
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#f0f2f5",
          ...getHeightStyles(),
        }}
      >
        {/* Left Sidebar Skeleton */}
        <Box
          sx={{
            width: "72px",
            backgroundColor: "#f0f2f5",
            borderRight: "1px solid #e9edef",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px 0",
            gap: "8px",
          }}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={`left-sidebar-skeleton-${index}`}
              variant="circular"
              width={48}
              height={48}
            />
          ))}
        </Box>

        {/* Chat Sidebar Skeleton */}
        <Box
          sx={{
            width: "30%",
            minWidth: "400px",
            maxWidth: "400px",
            backgroundColor: "#ffffff",
            borderRight: "1px solid #e9edef",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              height: "60px",
              display: "flex",
              alignItems: "center",
              padding: "0 16px",
              borderBottom: "1px solid #e9edef",
              backgroundColor: "#f0f2f5",
            }}
          >
            <Skeleton variant="text" width="60%" height={24} />
          </Box>
          <Box sx={{ p: 2 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={40}
              sx={{ mb: 2 }}
            />
            {Array.from({ length: 8 }).map((_, index) => (
              <Box key={`chat-sidebar-skeleton-${index}`} sx={{ mb: 2 }}>
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="60%" height={16} />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Main Chat Area Skeleton */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#f0f2f5",
          }}
        >
          <Box
            sx={{
              height: "60px",
              display: "flex",
              alignItems: "center",
              padding: "0 16px",
              borderBottom: "1px solid #e9edef",
              backgroundColor: "#ffffff",
            }}
          >
            <Skeleton variant="text" width="40%" height={24} />
          </Box>
          <Box sx={{ flex: 1, p: 2 }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Box key={`main-chat-skeleton-${index}`} sx={{ mb: 2 }}>
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="50%" height={16} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  const handleSearchToggle = (isOpen: boolean, type: PanelType = "search") => {
    setSearchOpen(isOpen);
    setSearchType(type);
  };

  const handleCloseSearch = () => {
    setSearchOpen(false);
  };

  return (
    <Container>
      <LeftSidebar />
      {sidebarType && (
        <SidebarContainer
          sx={{
            width: `${sidebarWidth}px`,
            minWidth: `${sidebarMinWidth}px`,
            maxWidth: `${sidebarMaxWidth}px`,
            position: "relative",
            transition: isSidebarResizing ? "none" : "width 0.15s ease-out",
            "@media (max-width: 600px)": {
              width: "calc(100% - 50px)",
              minWidth: "calc(100% - 50px)",
              maxWidth: "calc(100% - 50px)",
              left: "50px",
              backgroundColor: "#fff",
              border: "1px solid #e9edef",
            },
          }}
        >
          {/* Handle de redimensionamento (lado direito do sidebar) */}
          <ResizeHandle
            onMouseDown={handleSidebarMouseDown}
            onDoubleClick={handleSidebarDoubleClick}
            isResizing={isSidebarResizing}
            position="right"
          />

          {sidebarType === "user_profile" && <UserProfile key="user-profile" />}
          {sidebarType === "conversations" && (
            <ChatSidebar key="conversations" />
          )}
          {sidebarType === "settings" && (
            <CompanySettings key="company-settings" />
          )}
          {sidebarType === "profile" && (
            <CompanyProfile key="company-profile" />
          )}
          {sidebarType === "templates" && (
            <TemplatesSidebar key="templates" isOpen={true} />
          )}
          {sidebarType === "bulk_send_manager" && (
            <BulkSendManager key="bulk-send-manager" />
          )}
          {sidebarType === "lead_contacts" && (
            <LeadContacts key="lead-contacts" />
          )}
          {sidebarType === "ai_control_manager" && (
            <AIControlManager key="ai-control-manager" />
          )}
          {sidebarType === "phrase_config_manager" && (
            <PhraseConfigManager key="phrase-config-manager" />
          )}
          {sidebarType === "automation_manager" && (
            <AutomationManager key="automation-manager" />
          )}
        </SidebarContainer>
      )}
      <MainContent>
        <DialogWrapper isHidden={false}>
          <ChatDialog
            onSearchToggle={handleSearchToggle}
            isSearchOpen={searchOpen}
            infoPanelWidth={shouldShowInfoPanel ? infoPanelWidth : 350}
            isInfoPanelResizing={isInfoPanelResizing}
          />
        </DialogWrapper>
        <InfoPanelWrapper
          isSmallScreen={isSmallScreen}
          isSearchOpen={shouldShowInfoPanel}
          isResizing={isInfoPanelResizing}
          sx={{
            width: shouldShowInfoPanel
              ? isSmallScreen
                ? "100%"
                : `${infoPanelWidth}px`
              : isSmallScreen
                ? "100%"
                : "350px",
            minWidth: shouldShowInfoPanel
              ? isSmallScreen
                ? "100%"
                : `${infoPanelMinWidth}px`
              : isSmallScreen
                ? "100%"
                : "350px",
            maxWidth: shouldShowInfoPanel
              ? isSmallScreen
                ? "100%"
                : `${infoPanelMaxWidth}px`
              : isSmallScreen
                ? "100%"
                : "350px",
            transition: isInfoPanelResizing ? "none" : "width 0.15s ease-out",
            "@media (max-width: 600px)": {
              width: "100%",
              minWidth: "100%",
              maxWidth: "100%",
            },
          }}
        >
          {shouldShowInfoPanel && (
            <>
              {/* Handle de redimensionamento (lado esquerdo do InfoPanel) */}
              <ResizeHandle
                onMouseDown={handleInfoPanelMouseDown}
                onDoubleClick={handleInfoPanelDoubleClick}
                isResizing={isInfoPanelResizing}
                position="left"
              />
              <InfoPanel
                isOpen={shouldShowInfoPanel}
                onClose={handleCloseSearch}
                type={searchType}
              />
            </>
          )}
        </InfoPanelWrapper>
      </MainContent>
    </Container>
  );
};

export default Messenger;
